"use client";

import { motion } from "framer-motion";
import { PortableText } from "next-sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { useCountdown } from "@/lib/utilHooks";
import NextImage from "next/image";
import { GROQImageLoader } from "@/sanity/lib/definitions";
import { PageAnniversarySchema } from "@/sanity/pages/PageAnniversaryTemplate";
import Image from "@/helpers/Image";
import Countdown from "@/components/global/countdown/Countdown";
import goldLeaf from "@/public/anniversary/goldLeaf.svg";

const ease = [0.25, 0.1, 0.25, 1] as const;

function CountdownUnit({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex flex-col items-center gap-[1rem] w-[8rem]">
      <p className="mb-0 text-[6rem] leading-none tracking-[-0.12rem] font-medium tabular-nums">
        {value}
      </p>
      <p className="mb-0 text-[1.75rem] leading-none uppercase tracking-[-0.0875rem] text-white/50">
        {label}
      </p>
    </div>
  );
}

export default function HeroSection({ data }: { data: PageAnniversarySchema }) {
  const { days, hours, minutes, seconds } = useCountdown(
    data.countdownDate ?? "",
  );

  return (
    <section className="relative w-full overflow-hidden bg-white">
      {/* Ornamental background image */}
      {data.heroBackgroundImage && (
        <div className="absolute inset-0 pointer-events-none z-0">
          <Image
            src={data.heroBackgroundImage}
            alt=""
            className="!w-full object-cover object-top opacity-20"
          />
          {/*<div className="absolute inset-0 bg-gradient-to-b from-transparent from-[60%] to-white" />*/}
        </div>
      )}

      <div className="relative z-10 mx-auto w-[min(150.5rem,100%)] px-[3rem] lg:px-0 pb-[5rem] lg:pb-0">
        {/* Main two-column layout */}
        <div className="flex flex-col lg:flex-row items-center justify-between gap-[4rem] pt-[14rem] lg:pt-[16rem] pb-[6rem] lg:pb-[8rem]">
          {/* Left: text content */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease }}
            className="flex flex-col gap-[2.125rem] lg:max-w-[70.25rem]"
          >
            {data.heroChip && (
              <p className="mb-0 font-blacker italic text-[2rem] leading-[1.5] text-black">
                {data.heroChip}
              </p>
            )}

            {data.heroTitle && (
              <div className="richtext richtext-start max-sm:prose-headings:text-[4.85rem] max-sm:tracking-[-0.3rem]">
                <PortableText
                  value={data.heroTitle}
                  components={richTextComponent}
                />
              </div>
            )}

            {data.heroBody && (
              <div
                className="richtext richtext-start prose-p:mb-0 prose-p:text-[2.25rem] prose-p:leading-[1.5] prose-p:text-black/70 max-w-[66.75rem]"
                style={{ textWrapStyle: "pretty" }}
              >
                <PortableText
                  value={data.heroBody}
                  components={richTextComponent}
                />
              </div>
            )}

            <div className="flex flex-col gap-[2.375rem] items-center lg:items-start">
              {data.heroCTA && (
                <div className="w-full lg:w-fit lg:min-w-[29.75rem]">
                  <CTAButton data={data.heroCTA} />
                </div>
              )}

              {/* Trustpilot */}
              {(data.trustpilotImage || data.trustpilotText) && (
                <div className="flex items-center gap-[1.65rem]">
                  {data.trustpilotImage && (
                    <Image
                      src={data.trustpilotImage}
                      alt="Trustpilot rating"
                      className="!h-[2.25rem] !w-auto shrink-0"
                    />
                  )}
                  {data.trustpilotText && (
                    <p className="mb-0 font-spaceMono font-semibold text-[1.5rem] leading-[1.33] uppercase text-black">
                      {data.trustpilotText}
                    </p>
                  )}
                </div>
              )}
            </div>
          </motion.div>

          {/* Right: image + countdown */}
          <motion.div
            initial={{ opacity: 0, x: 32 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, ease, delay: 0.1 }}
            className="relative w-full lg:w-[60.75rem] flex-shrink-0"
          >
            <div className="relative w-full">
              {data.heroImage && (
                <Image
                  src={data.heroImage}
                  alt=""
                  className="object-cover object-center"
                />
              )}
            </div>

            {/* Countdown card */}
            {data.countdownDate && (
              <div className="relative w-full bg-[#001b14] px-[4rem] py-[3rem] z-10">
                {/* Green accent bar */}
                <div className="absolute top-[-0.75rem] left-1/2 -translate-x-1/2 h-[0.75rem] w-[41.5rem] bg-[#3b6162]" />

                {data.countdownLabel && (
                  <p className="mb-0 font-geist font-medium text-[1.75rem] text-white/50 uppercase leading-none tracking-[0.05rem] text-center">
                    {data.countdownLabel}
                  </p>
                )}

                <div className="flex items-center gap-[1.625rem] justify-center mt-[2rem] font-geist font-medium text-white">
                  <CountdownUnit value={days} label="Tage" />
                  <span className="text-[5rem] text-white/40 leading-none tracking-[-0.25rem] pb-[1rem]">
                    :
                  </span>
                  <CountdownUnit value={hours} label="STD" />
                  <span className="text-[5rem] text-white/40 leading-none tracking-[-0.25rem] pb-[1rem]">
                    :
                  </span>
                  <CountdownUnit value={minutes} label="MIN" />
                  <span className="text-[5rem] text-white/40 leading-none tracking-[-0.25rem] pb-[1rem]">
                    :
                  </span>
                  <CountdownUnit value={seconds} label="SEK" />
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* Stats bar */}
        {!!data.heroStats?.length && (
          <div className="border border-black/10 flex flex-wrap lg:flex-nowrap items-stretch mb-[6rem] lg:mb-[10rem]">
            {data.heroStats.map((stat, i) => (
              <div
                key={stat._key ?? i}
                className="w-1/2 lg:flex-1 flex items-center justify-center gap-[0.5rem] lg:gap-[1.5rem] px-[1.5rem] py-[3rem] lg:py-[5rem]"
              >
                <NextImage
                  src={goldLeaf}
                  alt=""
                  width={33}
                  height={48}
                  className="flex-shrink-0"
                />
                <div className="flex flex-col items-center text-center gap-[0.5rem]">
                  <p className="mb-0 font-geist font-medium text-[2.5rem] lg:text-[3rem] leading-none text-[#003828] tracking-[-0.15rem]">
                    {stat.value}
                  </p>
                  <p className="mb-0 font-geist font-normal text-[2rem] leading-[1.5] text-black/70 tracking-[-0.1rem]">
                    {stat.label}
                  </p>
                </div>
                <NextImage
                  src={goldLeaf}
                  alt=""
                  width={33}
                  height={48}
                  className="flex-shrink-0 scale-x-[-1]"
                />
              </div>
            ))}
          </div>
        )}

        <div className="flex lg:hidden flex-col gap-[2.375rem] items-center lg:items-start">
          {data.mobileTextAboveCTA && (
            <p
              className="lg:hidden mb-0 font-geist-mono uppercase font-semibold text-[1.5rem] leading-[1.5] text-black text-center"
              style={{ textWrapStyle: "balance" }}
            >
              {data.mobileTextAboveCTA}
            </p>
          )}
          {data.heroCTA && (
            <div className="w-full lg:w-fit lg:min-w-[29.75rem]">
              <CTAButton data={data.heroCTA} />
            </div>
          )}

          {/* Trustpilot */}
          {(data.trustpilotImage || data.trustpilotText) && (
            <div className="flex items-center gap-[1.65rem]">
              {data.trustpilotImage && (
                <Image
                  src={data.trustpilotImage}
                  alt="Trustpilot rating"
                  className="!h-[2.25rem] !w-auto shrink-0"
                />
              )}
              {data.trustpilotText && (
                <p className="mb-0 font-spaceMono font-semibold text-[1.5rem] leading-[1.33] uppercase text-black">
                  {data.trustpilotText}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
